import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import StudentInfoStep from './StudentInfoStep';
import QuizStep from './QuizStep';
import ResultsStep from './ResultsStep';

interface UserData {
  phone: string;
  countryCode: string;
  testType: 'english' | 'math';
  testLanguage: 'english' | 'arabic';
  studyLocation: string;
  nationality: string;
  studyField: string;
  grade: string;
  schoolType: 'private' | 'government';
  schoolName: string;
}

interface QuizAnswer {
  questionId: number;
  selectedOption: number;
  isCorrect: boolean;
}

type Step = 'phone' | 'otp' | 'test-selection' | 'math-language' | 'student-info' | 'quiz' | 'results';

const QuizApp: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>('phone');
  const [userData, setUserData] = useState<UserData>({
    phone: '',
    countryCode: '+965',
    testType: 'english',
    testLanguage: 'english',
    studyLocation: '',
    nationality: '',
    studyField: '',
    grade: '',
    schoolType: 'private',
    schoolName: ''
  });
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswer[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes in seconds
  const [isRTL, setIsRTL] = useState(false);

  // Timer effect
  useEffect(() => {
    if (currentStep === 'quiz' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleQuizSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentStep, timeLeft]);

  // RTL effect
  useEffect(() => {
    setIsRTL(userData.testLanguage === 'arabic');
    document.dir = userData.testLanguage === 'arabic' ? 'rtl' : 'ltr';
  }, [userData.testLanguage]);

  const getProgressPercentage = () => {
    const steps = ['phone', 'otp', 'test-selection', 'student-info', 'quiz', 'results'];
    if (userData.testType === 'math') {
      steps.splice(3, 0, 'math-language');
    }
    const currentIndex = steps.indexOf(currentStep);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handlePhoneSubmit = () => {
    if (userData.phone.length >= 8) {
      setCurrentStep('otp');
    }
  };

  const handleOtpSubmit = () => {
    if (otp.every(digit => digit !== '')) {
      setCurrentStep('test-selection');
    }
  };

  const handleTestSelection = (testType: 'english' | 'math') => {
    setUserData(prev => ({ ...prev, testType }));
    if (testType === 'math') {
      setCurrentStep('math-language');
    } else {
      setUserData(prev => ({ ...prev, testLanguage: 'english' }));
      setCurrentStep('student-info');
    }
  };

  const handleMathLanguageSelection = (language: 'english' | 'arabic') => {
    setUserData(prev => ({ ...prev, testLanguage: language }));
    setCurrentStep('student-info');
  };

  const handleStudentInfoSubmit = () => {
    setCurrentStep('quiz');
  };

  const handleQuizSubmit = () => {
    setCurrentStep('results');
  };

  const goBack = () => {
    switch (currentStep) {
      case 'otp':
        setCurrentStep('phone');
        break;
      case 'test-selection':
        setCurrentStep('otp');
        break;
      case 'math-language':
        setCurrentStep('test-selection');
        break;
      case 'student-info':
        if (userData.testType === 'math') {
          setCurrentStep('math-language');
        } else {
          setCurrentStep('test-selection');
        }
        break;
      case 'quiz':
        setCurrentStep('student-info');
        break;
      case 'results':
        setCurrentStep('quiz');
        break;
    }
  };

  return (
    <div className={`vh-100 d-flex flex-column ${isRTL ? 'text-end' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header with Logo and Progress */}
      <div className="container-fluid bg-white shadow-sm py-3">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-2">
              {currentStep !== 'phone' && (
                <button 
                  className="btn btn-link p-0 text-dark"
                  onClick={goBack}
                >
                  <i className={`bi ${isRTL ? 'bi-arrow-right' : 'bi-arrow-left'} fs-4`}></i>
                </button>
              )}
            </div>
            <div className="col-8 text-center">
              <img 
                src="https://storage.googleapis.com/uapply/logo-landing.png" 
                alt="Logo" 
                style={{ maxHeight: '50px' }}
                className="img-fluid"
              />
            </div>
            <div className="col-2"></div>
          </div>
          <div className="row mt-3">
            <div className="col-12">
              <div className="progress" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              {currentStep === 'phone' && (
                <PhoneStep 
                  userData={userData}
                  setUserData={setUserData}
                  onSubmit={handlePhoneSubmit}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 'otp' && (
                <OtpStep 
                  otp={otp}
                  setOtp={setOtp}
                  onSubmit={handleOtpSubmit}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 'test-selection' && (
                <TestSelectionStep 
                  onSelect={handleTestSelection}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 'math-language' && (
                <MathLanguageStep 
                  onSelect={handleMathLanguageSelection}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 'student-info' && (
                <StudentInfoStep 
                  userData={userData}
                  setUserData={setUserData}
                  onSubmit={handleStudentInfoSubmit}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 'quiz' && (
                <QuizStep 
                  userData={userData}
                  timeLeft={timeLeft}
                  formatTime={formatTime}
                  currentQuestionIndex={currentQuestionIndex}
                  setCurrentQuestionIndex={setCurrentQuestionIndex}
                  quizAnswers={quizAnswers}
                  setQuizAnswers={setQuizAnswers}
                  onSubmit={handleQuizSubmit}
                  isRTL={isRTL}
                />
              )}
              {currentStep === 'results' && (
                <ResultsStep 
                  userData={userData}
                  quizAnswers={quizAnswers}
                  isRTL={isRTL}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Phone Step Component
const PhoneStep: React.FC<{
  userData: UserData;
  setUserData: React.Dispatch<React.SetStateAction<UserData>>;
  onSubmit: () => void;
  isRTL: boolean;
}> = ({ userData, setUserData, onSubmit, isRTL }) => {
  const countryCodes = [
    { code: '+965', country: 'Kuwait' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+971', country: 'UAE' },
    { code: '+974', country: 'Qatar' },
    { code: '+973', country: 'Bahrain' },
    { code: '+968', country: 'Oman' },
  ];

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h2 className="fw-bold fs-5 text-center mb-4">
          {isRTL ? 'أدخل رقم هاتفك' : 'Enter your phone number'}
        </h2>
        
        <div className="mb-3">
          <label className="form-label">
            {isRTL ? 'كود الدولة' : 'Country Code'}
          </label>
          <select 
            className="form-select"
            value={userData.countryCode}
            onChange={(e) => setUserData(prev => ({ ...prev, countryCode: e.target.value }))}
          >
            {countryCodes.map(country => (
              <option key={country.code} value={country.code}>
                {country.code} {country.country}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="form-label">
            {isRTL ? 'رقم الهاتف' : 'Phone Number'}
          </label>
          <input
            type="tel"
            className="form-control"
            placeholder={isRTL ? 'أدخل رقم هاتفك' : 'Enter phone number'}
            value={userData.phone}
            onChange={(e) => setUserData(prev => ({ ...prev, phone: e.target.value }))}
          />
        </div>

        <button 
          className="btn btn-primary w-100 rounded-pill"
          onClick={onSubmit}
          disabled={userData.phone.length < 8}
        >
          {isRTL ? 'إرسال رمز التحقق' : 'Send OTP'}
        </button>
      </div>
    </div>
  );
};

// OTP Step Component
const OtpStep: React.FC<{
  otp: string[];
  setOtp: React.Dispatch<React.SetStateAction<string[]>>;
  onSubmit: () => void;
  isRTL: boolean;
}> = ({ otp, setOtp, onSubmit, isRTL }) => {
  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h2 className="fw-bold fs-5 text-center mb-4">
          {isRTL ? 'أدخل رمز التحقق' : 'Enter verification code'}
        </h2>
        
        <p className="text-center text-muted mb-4">
          {isRTL ? 'تم إرسال رمز التحقق إلى هاتفك' : 'Verification code sent to your phone'}
        </p>

        <div className="d-flex justify-content-center gap-2 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              className="form-control text-center"
              style={{ width: '50px', height: '50px' }}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              maxLength={1}
            />
          ))}
        </div>

        <button 
          className="btn btn-primary w-100 rounded-pill"
          onClick={onSubmit}
          disabled={otp.some(digit => digit === '')}
        >
          {isRTL ? 'تحقق' : 'Verify'}
        </button>
      </div>
    </div>
  );
};

// Test Selection Step Component
const TestSelectionStep: React.FC<{
  onSelect: (testType: 'english' | 'math') => void;
  isRTL: boolean;
}> = ({ onSelect, isRTL }) => {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h2 className="fw-bold fs-5 text-center mb-4">
          {isRTL ? 'أي اختبار تريد أن تأخذ؟' : 'Which test do you want to take?'}
        </h2>
        
        <div className="d-grid gap-3">
          <button 
            className="btn btn-outline-primary btn-lg rounded-pill d-flex align-items-center justify-content-center gap-2"
            onClick={() => onSelect('english')}
          >
            <span>🇬🇧</span>
            <span>{isRTL ? 'الإنجليزية' : 'English'}</span>
          </button>
          
          <button 
            className="btn btn-outline-primary btn-lg rounded-pill d-flex align-items-center justify-content-center gap-2"
            onClick={() => onSelect('math')}
          >
            <span>📐</span>
            <span>{isRTL ? 'الرياضيات' : 'Math'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Math Language Step Component
const MathLanguageStep: React.FC<{
  onSelect: (language: 'english' | 'arabic') => void;
  isRTL: boolean;
}> = ({ onSelect, isRTL }) => {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body p-4">
        <h2 className="fw-bold fs-5 text-center mb-4">
          {isRTL ? 'بأي لغة تريد أن تأخذ الاختبار؟' : 'Do you want to take it in English or Arabic?'}
        </h2>
        
        <div className="d-grid gap-3">
          <button 
            className="btn btn-outline-primary btn-lg rounded-pill d-flex align-items-center justify-content-center gap-2"
            onClick={() => onSelect('english')}
          >
            <span>🇬🇧</span>
            <span>English</span>
          </button>
          
          <button 
            className="btn btn-outline-primary btn-lg rounded-pill d-flex align-items-center justify-content-center gap-2"
            onClick={() => onSelect('arabic')}
          >
            <span>🇰🇼</span>
            <span>عربي</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizApp;
